import { Builder } from '@builder.io/react'
import CdnImage from 'components/util/CdnImage'
import React, { useEffect, useState } from 'react'

import { triggerTagging } from 'utils/common-utils'
import CustomHeading, { headingTypePropObj, headingClassPropObj } from '../common/heading'

interface IStepItem {
  stepTitle: string
  stepDescription: string
  stepDescriptionTwo: string
  builderStepNumberImage: any
  builderStepImage: any
  builderStepImageMobile: any
}

const FourStepSection = ({
  backgroundColor,
  backgroundWidthFull = false,
  stepItems,
  stepBarEnabled = false,
  stepBarTopAligned,
  smallerImagesEnabled = false,
  smallerImageWidth,
  stepsSameOrientationMobile = false,
  footerBackground,
  footerBackgroundMobile,
  headingText,
  headingTextColor,
  subHeading,
  headingClass,
  headingType,
  ctaButtonShow,
  coupon = ''
}: IStepItem) => {
  const [scrollToSection, setScrollToSection] = useState<HTMLElement | null>(null)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Find the first element with an ID containing "Route"
      const matchingElement = Array.from(document.querySelectorAll<HTMLElement>('[id]')).find(el =>
        el.id.includes('Route')
      )

      if (matchingElement) {
        setScrollToSection(matchingElement as HTMLElement)
      }
    }
  }, [])

  const getImageWidth = () => {
    const addPx = value => (typeof value === 'string' && value.endsWith('px') ? value : `${value}px`)

    if (smallerImageWidth) {
      return smallerImageWidth ? addPx(smallerImageWidth) : '100%'
    }

    return '100%'
  }

  return (
    <>
      <div
        className={`flex flex-col ${backgroundWidthFull ? 'w-full' : 'lg:w-10/12 lg:rounded-t-3xl'} mx-auto pt-8`}
        style={{ backgroundColor: backgroundColor || '#ffffff' }}
      >
        <div
          className={`flex flex-col mx-auto justify-center w-11/12 items-center mt-6 mb-2 ${stepBarEnabled ? '' : 'lg:my-8'}`}
        >
          <CustomHeading
            classList="text-center text-4xl"
            headingClass={headingClass}
            headingType={headingType}
            color={headingTextColor}
          >
            {headingText}
          </CustomHeading>
          <div
            className={`enable-list-styles mt-4 h-full text-center text-base font-medium font-sans w-full justify-center`}
            dangerouslySetInnerHTML={{ __html: subHeading }}
          />
        </div>
        {stepBarEnabled && (
          <div className="hidden lg:flex w-11/12 mx-auto items-center mb-6 relative">
            <div className="flex w-full justify-between items-center relative z-10">
              {stepItems?.map((step: Step, i: number) => (
                <div key={i} className="flex-1 flex justify-center">
                  <img src={step.builderStepNumberImage} alt={`Step ${i + 1}`} className="h-16 w-16 object-contain" />
                </div>
              ))}
            </div>
            {/* Step bar through icons */}
            <div className="absolute top-1/2 left-[calc(8%+4rem)] 2xl:left-[calc(8%+5rem)] right-[calc(8%+4rem)] 2xl:right-[calc(8%+5rem)] h-2 bg-[#004355] z-0" />
          </div>
        )}

        {/* Desktop view */}
        <div className="flex-row w-11/12 mx-auto justify-center hidden lg:flex">
          {stepItems?.map((step: Step, i: number) => (
            <div key={i} className="mx-auto !flex h-full max-w-sm justify-center w-11/12">
              <div className="mx-2 my-0 flex gap-2 w-full flex-col rounded-2xl">
                <CdnImage
                  className={`w-full object-contain md:max-h-full rounded-2xl mx-auto ${smallerImagesEnabled ? 'block justify-center' : 'flex'}`}
                  url={step.builderStepImage}
                  alt={step.stepImage?.imageAltText}
                  steps={[400, 300]}
                  style={{
                    width: smallerImagesEnabled ? (smallerImageWidth ? getImageWidth() : '200px') : '400px',
                    height: 'auto',
                    maxHeight: '100%',
                    maxWidth: '100%',
                    aspectRatio: '1 / 1'
                  }}
                />
                <div className={`flex h-full flex-col ${smallerImagesEnabled ? 'items-center' : 'items-start'}`}>
                  <div className="font-sans font-bold text-lg text-base pb-4">{step.stepTitle}</div>
                  <div className="font-sans font-normal text-base">{step.stepDescription}</div>
                  {step.stepDescriptionTwo && <div dangerouslySetInnerHTML={{ __html: step.stepDescriptionTwo }} />}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile view */}
        <div className="flex-col w-full mx-auto justify-center flex lg:hidden">
          {stepItems?.map((step: Step, i: number) => {
            const isFirst = i === 0
            const isLast = i === stepItems.length - 1

            return (
              <div key={i} className="flex w-11/12 mx-auto">
                {/* Step bar + icon */}
                {stepBarEnabled && (
                  <div className="flex flex-col items-center mr-4 relative w-10">
                    {/* Top bar (invisible for first step) */}
                    <div
                      className={`${stepBarTopAligned ? 'flex-0 pt-8' : 'flex-1'} w-1 ${isFirst ? 'bg-transparent' : 'bg-[#004355]'}`}
                    />

                    {/* Step icon */}
                    <div className="h-10 w-10 flex items-center justify-center z-10 shrink-0">
                      <img
                        src={step.builderStepNumberImage}
                        alt={`Step ${i + 1}`}
                        className="h-10 w-10 object-contain"
                      />
                    </div>

                    {/* Bottom bar (invisible for last step) */}
                    <div className={`flex-1 w-1 ${isLast ? 'bg-transparent' : 'bg-[#004355]'}`} />
                  </div>
                )}

                {/* Step content */}
                <div className="flex flex-col gap-2 w-full py-4">
                  <div
                    className={`flex ${
                      (!stepBarEnabled && !stepsSameOrientationMobile) && i % 2 !== 0
                        ? 'flex-row-reverse items-start'
                        : `flex-row ${stepBarTopAligned ? 'items-start' : 'items-center'}`
                    } gap-2`}
                  >
                    {/* Step image */}
                    <div className={`${smallerImagesEnabled ? 'w-1/5' : 'w-2/5'} flex items-center justify-center`}>
                      <CdnImage
                        className="block w-full object-cover rounded-2xl"
                        url={step.builderStepImageMobile}
                        alt={step.stepImage?.imageAltText}
                        steps={[400, 300]}
                        style={{
                          width: '400px',
                          height: 'auto',
                          maxHeight: '100%',
                          maxWidth: '100%'
                        }}
                      />
                    </div>

                    {/* Step text */}
                    <div
                      className={`${smallerImagesEnabled ? 'w-4/5' : 'w-3/5'} flex flex-col items-start justify-center px-1`}
                    >
                      <div className="text-left font-sans font-bold text-base pb-1">{step.stepTitle}</div>
                      <div className="text-left font-sans font-normal text-sm">{step.stepDescription}</div>
                      {step.stepDescriptionTwo && (
                        <div
                          dangerouslySetInnerHTML={{
                            __html: step.stepDescriptionTwo
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {ctaButtonShow && (
          <button
            type="button"
            className={` mx-auto mt-4 lg:mt-10 mb-8 flex w-4/5 justify-center rounded-3xl border border-transparent bg-primary px-4 py-2 text-base font-medium hover:underline md:text-lg md:w-1/2 lg:w-1/4`}
            onClick={() => {
              if (scrollToSection) {
                triggerTagging(`four_step_section_cta_${scrollToSection.id}`, `${scrollToSection.id}`)
                scrollToSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
              } else {
                window.location.href = `/signup/dog-details/${coupon ? `?coupon=${coupon}` : ''}`
              }
            }}
            style={{ color: '#fff' }}
          >
            {scrollToSection ? 'Get Started' : 'Create Your Plan'}
          </button>
        )}
      </div>
      {footerBackground && (
        <div
          className={`relative mx-auto hidden w-10/12 bg-contain bg-top bg-no-repeat lg:block ${
            footerBackground ? 'h-28 2xl:h-56' : 'h-8'
          }`}
          style={{
            backgroundImage: `url(${footerBackground})`
          }}
        />
      )}

      {footerBackgroundMobile && (
        <div
          className={`relative mx-auto lg:hidden w-full bg-cover bg-top bg-no-repeat block ${
            footerBackgroundMobile ? 'h-20 md:h-24' : 'h-8'
          }`}
          style={{
            backgroundImage: `url(${footerBackgroundMobile})`
          }}
        />
      )}
    </>
  )
}

export default FourStepSection

Builder.registerComponent(FourStepSection, {
  name: 'FourStepSection',
  inputs: [
    {
      name: 'backgroundColor',
      type: 'color',
      defaultValue: 'white'
    },
    {
      name: 'backgroundWidthFull',
      type: 'boolean',
      defaultValue: false
    },
    {
      ...headingTypePropObj
    },
    {
      ...headingClassPropObj
    },
    {
      name: 'headingText',
      type: 'text',
      defaultValue: 'Javascript for Beginners'
    },
    {
      name: 'headingTextColor',
      type: 'color',
      defaultValue: 'black'
    },
    {
      name: 'subHeading',
      type: 'richText',
      defaultValue: ''
    },
    {
      name: 'stepItems',
      type: 'list',
      subFields: [
        { name: 'stepTitle', type: 'text', defaultValue: 'Sales Support' },
        {
          name: 'stepDescription',
          type: 'text',
          defaultValue: 'Lorem ipsum, dolor sit amet consectetur adipisicing elit. Maiores impedit perferendis'
        },
        {
          name: 'stepDescriptionTwo',
          type: 'richText',
          defaultValue: ''
        },
        {
          name: 'builderStepNumberImage',
          type: 'file',
          allowedFileTypes: ['jpeg', 'png', 'svg']
        },
        {
          name: 'builderStepImage',
          type: 'file',
          allowedFileTypes: ['jpeg', 'png', 'svg']
        },
        {
          name: 'builderStepImageMobile',
          type: 'file',
          allowedFileTypes: ['jpeg', 'png', 'svg']
        }
      ]
    },
    {
      name: 'smallerImagesEnabled',
      type: 'boolean',
      defaultValue: false
    },
    {
      name: 'smallerImageWidth',
      type: 'string',
      defaultValue: ''
    },
    {
      name: 'stepBarEnabled',
      type: 'boolean',
      defaultValue: false
    },
    {
      name: 'stepsSameOrientationMobile',
      type: 'boolean',
      defaultValue: false
    },
    {
      name: 'stepBarTopAligned',
      type: 'boolean',
      defaultValue: false,
      helperText: 'Mobile only. Aligns the icons and images to the top.'
    },
    {
      name: 'ctaButtonShow',
      type: 'boolean',
      defaultValue: false
    },
    {
      name: 'coupon',
      type: 'text',
      defaultValue: ''
    },
    {
      name: 'footerBackground',
      type: 'file',
      defaultValue: null
    },
    {
      name: 'footerBackgroundMobile',
      type: 'file',
      defaultValue: null
    }
  ]
})
